import { useState } from 'react';

interface Notification {
  id: string;
  type: 'payment' | 'lead' | 'bid' | 'system' | 'reminder';
  title: string;
  message: string;
  timestamp: string;
  read: boolean;
}

const MOCK_NOTIFICATIONS: Notification[] = [
  { id: '1', type: 'payment', title: 'Payment Received', message: 'Horizon Films paid Invoice #1042 ($3,200.00).', timestamp: '2 hours ago', read: false },
  { id: '2', type: 'lead', title: 'New Lead', message: 'Wedding Video Package inquiry from Amanda & Jason via your website.', timestamp: '5 hours ago', read: false },
  { id: '3', type: 'bid', title: 'Bid Accepted', message: 'Your bid for "Corporate Brand Video" was accepted by TechCorp Inc.', timestamp: '1 day ago', read: false },
  { id: '4', type: 'reminder', title: 'Invoice Overdue', message: 'Invoice #1040 for Vertex Productions is 13 days overdue ($5,600.00).', timestamp: '1 day ago', read: true },
  { id: '5', type: 'system', title: 'System Update', message: 'New template editor is now available. Check it out in the Templates section.', timestamp: '2 days ago', read: true },
  { id: '6', type: 'lead', title: 'Lead Status Changed', message: '"Corporate Documentary" lead moved from New to Qualified.', timestamp: '3 days ago', read: true },
  { id: '7', type: 'payment', title: 'Payment Reminder Sent', message: 'Automatic payment reminder sent to Bloom Media for Invoice #1041.', timestamp: '4 days ago', read: true },
  { id: '8', type: 'bid', title: 'Bid Expired', message: 'Your bid for "Fitness Brand Content Series" to FlexFit Co. has expired.', timestamp: '5 days ago', read: true },
];

function typeIcon(type: Notification['type']): string {
  switch (type) {
    case 'payment': return '$';
    case 'lead': return '\u2606';
    case 'bid': return '\u2691';
    case 'system': return '\u2699';
    case 'reminder': return '!';
  }
}

function typeColor(type: Notification['type']): string {
  switch (type) {
    case 'payment': return 'var(--color-success)';
    case 'lead': return 'var(--color-info)';
    case 'bid': return 'var(--color-primary)';
    case 'system': return 'var(--color-text-muted)';
    case 'reminder': return 'var(--color-warning)';
  }
}

export function NotificationsPage() {
  const [notifications, setNotifications] = useState(MOCK_NOTIFICATIONS);
  const [filter, setFilter] = useState<'all' | 'unread'>('all');

  const unreadCount = notifications.filter((n) => !n.read).length;

  const filteredNotifications = notifications.filter(
    (n) => filter === 'all' || !n.read,
  );

  const markAllRead = () => {
    setNotifications(notifications.map((n) => ({ ...n, read: true })));
  };

  const toggleRead = (id: string) => {
    setNotifications(
      notifications.map((n) =>
        n.id === id ? { ...n, read: !n.read } : n,
      ),
    );
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', flexWrap: 'wrap', gap: '12px' }}>
        <div>
          <h1 style={{ fontSize: '1.5rem', fontWeight: 700, marginBottom: '4px' }}>Notifications</h1>
          <p style={{ color: 'var(--color-text-muted)', fontSize: '0.875rem' }}>
            {unreadCount > 0
              ? `You have ${unreadCount} unread notification${unreadCount !== 1 ? 's' : ''}.`
              : 'All caught up.'}
          </p>
        </div>
        {unreadCount > 0 && (
          <button
            onClick={markAllRead}
            style={{
              padding: '8px 16px',
              background: 'transparent',
              border: '1px solid var(--color-border)',
              borderRadius: 'var(--radius-md)',
              color: 'var(--color-text-muted)',
              fontSize: '0.8125rem',
              cursor: 'pointer',
            }}
          >
            Mark all as read
          </button>
        )}
      </div>

      {/* Filter tabs */}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
        {(['all', 'unread'] as const).map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            style={{
              padding: '8px 16px',
              borderRadius: 'var(--radius-md)',
              border: '1px solid var(--color-border)',
              background: filter === f ? 'var(--color-primary)' : 'transparent',
              color: filter === f ? '#fff' : 'var(--color-text-muted)',
              fontSize: '0.8125rem',
              fontWeight: 500,
              textTransform: 'capitalize',
              cursor: 'pointer',
            }}
          >
            {f}
            {f === 'unread' && unreadCount > 0 && (
              <span style={{
                marginLeft: '6px',
                padding: '1px 6px',
                borderRadius: '999px',
                fontSize: '0.6875rem',
                background: filter === f ? 'rgba(255,255,255,0.2)' : 'var(--color-primary)',
                color: '#fff',
              }}>
                {unreadCount}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Notification list */}
      <div
        style={{
          background: 'var(--color-surface)',
          border: '1px solid var(--color-border)',
          borderRadius: 'var(--radius-lg)',
          overflow: 'hidden',
        }}
      >
        {filteredNotifications.map((notification, index) => (
          <div
            key={notification.id}
            onClick={() => toggleRead(notification.id)}
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: '14px',
              padding: '16px 20px',
              borderBottom: index < filteredNotifications.length - 1 ? '1px solid var(--color-border)' : 'none',
              background: notification.read ? 'transparent' : 'rgba(99,102,241,0.04)',
              cursor: 'pointer',
            }}
          >
            {/* Icon */}
            <span
              style={{
                width: '36px',
                height: '36px',
                borderRadius: '50%',
                background: 'var(--color-bg)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '0.875rem',
                fontWeight: 600,
                color: typeColor(notification.type),
                flexShrink: 0,
                marginTop: '2px',
              }}
            >
              {typeIcon(notification.type)}
            </span>

            {/* Content */}
            <div style={{ flex: 1 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: '8px', marginBottom: '4px' }}>
                <span style={{ fontSize: '0.875rem', fontWeight: notification.read ? 400 : 600 }}>
                  {notification.title}
                </span>
                <span style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)', flexShrink: 0 }}>
                  {notification.timestamp}
                </span>
              </div>
              <p style={{ fontSize: '0.8125rem', color: 'var(--color-text-muted)', lineHeight: 1.5 }}>
                {notification.message}
              </p>
            </div>

            {/* Unread dot */}
            {!notification.read && (
              <span
                style={{
                  width: '8px',
                  height: '8px',
                  borderRadius: '50%',
                  background: 'var(--color-primary)',
                  flexShrink: 0,
                  marginTop: '8px',
                }}
              />
            )}
          </div>
        ))}

        {filteredNotifications.length === 0 && (
          <div style={{ padding: '48px', textAlign: 'center', color: 'var(--color-text-muted)' }}>
            {filter === 'unread' ? 'No unread notifications.' : 'No notifications yet.'}
          </div>
        )}
      </div>
    </div>
  );
}
